import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Typography,
  Box,
  Divider,
  Alert,
  LinearProgress,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { PictureAsPdf, Code, Download, Upload } from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./ExportModal.css";
import { getSymbolsUsedInPattern } from "../../utils/stitchSymbols";
import {
  compilePattern,
  compilePatternToText,
} from "../../services/patternCompiler";

const ExportModal = ({
  open,
  onClose,
  chart,
  patternTitle,
  compiledInstructions,
  onImportPattern,
  attribution,
  chartImageDataUrl,
  hideImportSection,
  inTheRound = false,
}) => {
  const [exportFormat, setExportFormat] = useState("pdf");
  const [includeChart, setIncludeChart] = useState(true);
  const [includeInstructions, setIncludeInstructions] = useState(true);
  const [includeTitle, setIncludeTitle] = useState(true);
  const [orientation, setOrientation] = useState("portrait");
  const [quality, setQuality] = useState("high");
  const [fileInputRef, setFileInputRef] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const [exportProgress, setExportProgress] = useState({
    current: 0,
    total: 0,
    message: "",
  });
  const [includeKey, setIncludeKey] = useState(true);
  const [abbrevNames, setAbbrevNames] = useState(false);
  const [includeDescriptions, setIncludeDescriptions] = useState(false);

  const handleFormatChange = (event) => {
    setExportFormat(event.target.value);
  };

  const handleOrientationChange = (event) => {
    setOrientation(event.target.value);
  };

  const handleQualityChange = (event) => {
    setQuality(event.target.value);
  };

  // Helper function to render instructions as HTML with proper formatting
  const renderInstructionsAsHTML = (instructionsText) => {
    const lines = instructionsText.split("\n");
    return lines
      .map((line) => {
        // Check if line starts with "Row" or "Rows" and make it bold
        const match = line.match(/^((?:Row|Rows)\s+\d+(?:-\d+)?):\s*(.*)$/);
        if (match) {
          const rowLabel = match[1];
          const instruction = match[2];
          return `<div style="margin-bottom: 4px;"><strong>${rowLabel}:</strong> ${instruction}</div>`;
        }
        return `<div style="margin-bottom: 4px;">${line}</div>`;
      })
      .join("");
  };

  const handleExport = async () => {
    const title = patternTitle || "Untitled Pattern";

    try {
      setIsExporting(true);
      setExportProgress({
        current: 0,
        total: 0,
        message: "Preparing export...",
      });

      switch (exportFormat) {
        case "pdf":
          await exportToPDF(
            title,
            includeChart,
            includeInstructions,
            includeTitle,
          );
          break;
        case "json":
          exportToJSON(title);
          break;
        default:
          console.error("Unknown export format:", exportFormat);
      }
    } catch (error) {
      console.error("Export failed:", error);
      setSnackbar({ open: true, message: "Export failed. Please try again.", severity: "error" });
    } finally {
      setIsExporting(false);
      setExportProgress({ current: 0, total: 0, message: "" });
      onClose();
    }
  };

  const exportToPDF = async (
    title,
    includeChart,
    includeInstructions,
    includeTitle,
  ) => {
    // Compile instructions with abbreviation preference if chart is available
    // If compiledInstructions is provided, use it; otherwise compile from chart
    let instructionsToUse = compiledInstructions;
    if (includeInstructions && chart && !compiledInstructions) {
      const compiled = compilePattern(chart, abbrevNames);
      instructionsToUse = compilePatternToText(compiled);
    } else if (includeInstructions && chart && compiledInstructions) {
      // Recompile with current abbreviation preference
      const compiled = compilePattern(chart, abbrevNames);
      instructionsToUse = compilePatternToText(compiled);
    }
    const pdf = new jsPDF(
      orientation === "landscape" ? "landscape" : "portrait",
    );

    // Set margins (0.5 inches = ~13mm for printer compatibility)
    const marginLeft = 13;
    const marginTop = 13;
    const marginRight = 13;
    const marginBottom = 13;
    const pageWidth = orientation === "landscape" ? 297 : 210; // A4 dimensions in mm
    const pageHeight = orientation === "landscape" ? 210 : 297;
    const contentWidth = pageWidth - marginLeft - marginRight;

    let yPosition = marginTop;

    // Add title if requested
    if (includeTitle) {
      pdf.setFontSize(20);
      pdf.setFont("helvetica", "bold");
      // Center the chart title on the page
      pdf.text(title, pageWidth / 2, yPosition, { align: "center" });
      yPosition += 14; // tighter spacing under main title
    }

    // Optional attribution line
    if (attribution) {
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(attribution, marginLeft, yPosition);
      yPosition += 6; // minimal space after attribution line
    }

    // Add chart if requested
    if (includeChart) {
      const chartElement = document.getElementById("chart-grid");
      if (chartElement) {
        const totalCells = chart.width * chart.height;

        // For large charts (>2,500 cells), use multi-page tiled export
        if (totalCells > 2500) {
          await exportLargeChartToPDF(
            pdf,
            title,
            includeInstructions,
            includeTitle,
            yPosition,
          );
          return;
        }

        // For smaller charts, use the original single-page export
        try {
          // Temporarily reset zoom for better capture
          const scaledContainer = chartElement.closest(
            ".chart-grid-scaled-container",
          );
          const originalTransform = scaledContainer?.style.transform;
          if (scaledContainer) {
            scaledContainer.style.transform = "scale(1)";
            scaledContainer.style.width = "100%";
            scaledContainer.style.height = "100%";
          }

          // Wait a moment for the DOM to update
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Determine scale based on quality setting
          const scaleMap = {
            low: 3,
            medium: 4,
            high: 6,
            ultra: 8,
          };
          const scale = scaleMap[quality] || 6;

          const canvas = await html2canvas(chartElement, {
            scale: scale,
            backgroundColor: "#ffffff",
            useCORS: true,
            allowTaint: true,
            logging: false,
            width: chartElement.scrollWidth,
            height: chartElement.scrollHeight,
          });

          // Restore original transform
          if (scaledContainer && originalTransform) {
            scaledContainer.style.transform = originalTransform;
          }

          const imgData = canvas.toDataURL("image/png");
          // Adjust max width based on content area
          const maxWidth = contentWidth - 20; // Leave some padding
          const imgWidth = Math.min(maxWidth, canvas.width);
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Check if we need a new page
          if (yPosition + imgHeight > pageHeight - marginBottom - 20) {
            pdf.addPage();
            yPosition = marginTop;
          }

          pdf.addImage(
            imgData,
            "PNG",
            marginLeft,
            yPosition,
            imgWidth,
            imgHeight,
          );
          yPosition += imgHeight + 20;
        } catch (error) {
          console.error("Error capturing chart:", error);
          pdf.setFontSize(12);
          pdf.text(
            "Error: Could not capture chart image",
            marginLeft,
            yPosition,
          );
          yPosition += 20;
        }
      } else {
        // Fallback: if a chart image data URL was provided (e.g., from Gallery), embed it
        if (chartImageDataUrl) {
          try {
            const img = await new Promise((resolve, reject) => {
              const image = new Image();
              image.crossOrigin = "anonymous";
              image.onload = () => resolve(image);
              image.onerror = reject;
              image.src = chartImageDataUrl;
            });
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const imgData = canvas.toDataURL("image/png");

            const maxWidth = contentWidth - 20;
            const imgWidth = Math.min(maxWidth, canvas.width);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            if (yPosition + imgHeight > pageHeight - marginBottom - 20) {
              pdf.addPage();
              yPosition = marginTop;
            }

            pdf.addImage(
              imgData,
              "PNG",
              marginLeft,
              yPosition,
              imgWidth,
              imgHeight,
            );
            yPosition += imgHeight + 20;
          } catch (e) {
            console.error("Fallback chart image failed:", e);
            pdf.setFontSize(12);
            pdf.text("Error: Chart element not found", marginLeft, yPosition);
            yPosition += 20;
          }
        } else {
          console.error("Chart element not found");
          pdf.setFontSize(12);
          pdf.text("Error: Chart element not found", marginLeft, yPosition);
          yPosition += 20;
        }
      }
    }

    // Add instructions if requested
    if (includeInstructions && instructionsToUse) {
      // Check if we need a new page
      if (yPosition > pageHeight - marginBottom - 50) {
        pdf.addPage();
        yPosition = marginTop;
      }

      // Render instructions as HTML element and convert to image for cleaner formatting
      // Include the header in the HTML so it stays with the content
      const instructionsHtml = `<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Written Instructions:</div>${renderInstructionsAsHTML(instructionsToUse)}`;
      const tempInstructionsContainer = document.createElement("div");
      tempInstructionsContainer.style.position = "absolute";
      tempInstructionsContainer.style.left = "-9999px";
      tempInstructionsContainer.style.top = "-9999px";
      tempInstructionsContainer.style.width = `${contentWidth}mm`;
      tempInstructionsContainer.style.padding = "10px";
      tempInstructionsContainer.style.backgroundColor = "#ffffff";
      tempInstructionsContainer.style.fontFamily = "monospace";
      tempInstructionsContainer.style.fontSize = "10px";
      tempInstructionsContainer.style.lineHeight = "1.8";
      tempInstructionsContainer.innerHTML = instructionsHtml;
      document.body.appendChild(tempInstructionsContainer);

      try {
        // Wait for rendering
        await new Promise((resolve) => setTimeout(resolve, 50));

        // Capture as image
        const canvas = await html2canvas(tempInstructionsContainer, {
          scale: 2,
          backgroundColor: "#ffffff",
          useCORS: true,
          allowTaint: true,
          logging: false,
        });

        const imgData = canvas.toDataURL("image/png");
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Check if image fits on current page
        if (yPosition + imgHeight > pageHeight - marginBottom - 20) {
          pdf.addPage();
          yPosition = marginTop;
        }

        // Handle multi-page instructions if needed
        let remainingHeight = imgHeight;
        let sourceY = 0;
        const maxPageHeight = pageHeight - marginBottom - yPosition - 10;

        while (remainingHeight > 0) {
          if (yPosition + remainingHeight > pageHeight - marginBottom - 20) {
            const pageImgHeight = Math.min(remainingHeight, maxPageHeight);
            const sourceHeight = (pageImgHeight / imgHeight) * canvas.height;

            // Create a temporary canvas for this page slice
            const pageCanvas = document.createElement("canvas");
            pageCanvas.width = canvas.width;
            pageCanvas.height = sourceHeight;
            const ctx = pageCanvas.getContext("2d");
            ctx.drawImage(
              canvas,
              0,
              sourceY,
              canvas.width,
              sourceHeight,
              0,
              0,
              canvas.width,
              sourceHeight,
            );

            const pageImgData = pageCanvas.toDataURL("image/png");
            pdf.addImage(
              pageImgData,
              "PNG",
              marginLeft,
              yPosition,
              imgWidth,
              pageImgHeight,
            );

            remainingHeight -= pageImgHeight;
            sourceY += sourceHeight;

            if (remainingHeight > 0) {
              pdf.addPage();
              yPosition = marginTop;
            } else {
              yPosition += pageImgHeight + 10;
            }
          } else {
            pdf.addImage(imgData, "PNG", marginLeft, yPosition, imgWidth, remainingHeight);
            yPosition += remainingHeight + 10;
            break;
          }
        }
      } catch (error) {
        console.error("Error rendering instructions:", error);
        // Fallback to plain text if HTML rendering fails
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        const lines = instructionsToUse.split("\n");
        lines.forEach((line) => {
          if (yPosition > pageHeight - marginBottom - 20) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(line, marginLeft, yPosition);
          yPosition += 6;
        });
      } finally {
        document.body.removeChild(tempInstructionsContainer);
      }
    }

    // Add stitch key if requested
    if (includeKey && chart) {
      // Check if we need a new page
      if (yPosition > pageHeight - marginBottom - 60) {
        pdf.addPage();
        yPosition = marginTop;
      }

      const used = getSymbolsUsedInPattern(chart);
      if (used.length > 0) {
        // extra padding above Stitch Key header
        yPosition += 6;
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text("Stitch Key", marginLeft, yPosition);
        yPosition += 12;

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");

        const colGap = 6;
        // Columns as percentages of available content width
        const col1Width = Math.max(10, Math.floor(contentWidth * 0.15)); // 15% for symbol
        const col2Width = Math.max(20, Math.floor(contentWidth * 0.2)); // 20% for name/abbrev
        const col3Width = Math.max(
          20,
          contentWidth - col1Width - col2Width - colGap * 2,
        ); // remainder for description

        // Symbol box should fit within col1
        const symbolBox = Math.max(8, Math.min(12, col1Width - 4));
        const renderSymbolToDataUrl = (symbol) => {
          const size = 32;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, size, size);
          ctx.fillStyle = "#2A2A2A";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font =
            '20px "Apple Symbols", "Segoe UI Symbol", "Noto Sans Symbols", sans-serif';
          ctx.fillText(symbol, size / 2, size / 2);
          return canvas.toDataURL("image/png");
        };

        used.forEach((item) => {
          if (!item) return;
          if (yPosition > pageHeight - marginBottom - 12) {
            pdf.addPage();
            yPosition = marginTop;
          }
          // Symbol (rendered via canvas to avoid missing glyphs)
          try {
            const dataUrl = renderSymbolToDataUrl(item.symbol);
            const boxTop = yPosition - symbolBox / 2;
            const imgSize = Math.max(6, symbolBox - 2);
            // border box for consistency
            pdf.rect(marginLeft, boxTop, symbolBox, symbolBox);
            // draw image with 1px padding inside the box
            pdf.addImage(
              dataUrl,
              "PNG",
              marginLeft + 1,
              boxTop + 1,
              imgSize,
              imgSize,
            );
          } catch (e) {
            // fallback: draw text if something fails
            const boxTop = yPosition - symbolBox / 2;
            pdf.rect(marginLeft, boxTop, symbolBox, symbolBox);
            pdf.text(
              item.symbol || "?",
              marginLeft + symbolBox / 2,
              yPosition,
              { align: "center" },
            );
          }

          // Name (abbrev or full)
          const nameText =
            abbrevNames && item.abbreviation
              ? `${item.abbreviation}`
              : item.meaning;
          pdf.text(nameText, marginLeft + col1Width + colGap, yPosition);

          // Description optional, wrapped
          if (includeDescriptions && item.description) {
            const lines = pdf.splitTextToSize(item.description, col3Width);
            // Draw description starting at col3
            const startY = yPosition - 4;
            lines.forEach((line, idx) => {
              const lineY = startY + idx * 5 + 4;
              pdf.text(
                line,
                marginLeft + col1Width + col2Width + colGap * 2,
                lineY,
              );
            });
            yPosition += Math.max(10, lines.length * 5 + 2);
          } else {
            yPosition += 10;
          }
        });
      }
    }

    // Save the PDF
    pdf.save(`${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf`);
  };

  // Export large charts as multi-page PDF with tiled sections
  // For charts larger than 2500 cells, split into 50x50 tile sections for optimal rendering
  // Each tile becomes a separate PDF page to ensure readability and print quality
  const exportLargeChartToPDF = async (
    pdf,
    title,
    includeInstructions,
    includeTitle,
    yPosition,
  ) => {
    // Calculate tile size (50x50 sections for optimal rendering and file size)
    const tileSize = 50;
    // Calculate how many tiles are needed in each direction
    const tilesX = Math.ceil(chart.width / tileSize);
    const tilesY = Math.ceil(chart.height / tileSize);
    const totalTiles = tilesX * tilesY;

    // Set margins (0.5 inches = ~13mm for printer compatibility)
    const marginLeft = 13;
    const marginTop = 13;
    const marginRight = 13;
    const marginBottom = 13;
    const pageWidth = orientation === "landscape" ? 297 : 210; // A4 dimensions in mm
    const pageHeight = orientation === "landscape" ? 210 : 297;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const contentHeight = pageHeight - marginTop - marginBottom;

    // Add overview page (tighter header spacing)
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text("Chart Overview", marginLeft, yPosition);
    yPosition += 12;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Pattern: ${title}`, marginLeft, yPosition);
    yPosition += 8;
    pdf.text(
      `Chart Size: ${chart.width} × ${chart.height} stitches`,
      marginLeft,
      yPosition,
    );
    yPosition += 8;
    pdf.text(
      `Export Format: ${tilesX} × ${tilesY} sections`,
      marginLeft,
      yPosition,
    );
    yPosition += 12;

    // Start sections on a fresh page to avoid overlap with overview
    pdf.addPage();
    yPosition = marginTop;

    pdf.setFontSize(10);
    pdf.text(
      "This chart has been split into multiple pages for optimal printing quality.",
      marginLeft,
      yPosition,
    );
    yPosition += 10;
    pdf.text(
      "Each section shows a 50×50 stitch area of the pattern.",
      marginLeft,
      yPosition,
    );
    yPosition += 20;

    // Create a temporary off-screen container for rendering each tile
    // This allows us to render each tile section separately without affecting the visible UI
    const tempContainer = document.createElement("div");
    tempContainer.style.position = "absolute";
    tempContainer.style.left = "-9999px";
    tempContainer.style.top = "-9999px";
    tempContainer.style.width = "2400px"; // 50 columns * 48px per cell
    tempContainer.style.height = "2400px"; // 50 rows * 48px per cell
    tempContainer.style.backgroundColor = "#ffffff";
    document.body.appendChild(tempContainer);

    try {
      // Export each tile as a separate PDF page
      for (let tileY = 0; tileY < tilesY; tileY++) {
        for (let tileX = 0; tileX < tilesX; tileX++) {
          const tileNumber = tileY * tilesX + tileX + 1;
          
          // Update progress indicator for user feedback
          setExportProgress({
            current: tileNumber,
            total: totalTiles,
            message: `Exporting section ${tileY + 1}-${tileX + 1} (${tileNumber}/${totalTiles})`,
          });

          // Add new page for each tile (first tile uses existing page)
          if (tileNumber > 1) {
            pdf.addPage();
          }

          // Generate HTML table content for this tile
          const tileContent = createTileContent(tileX, tileY, tileSize);
          tempContainer.innerHTML = tileContent;

          // Wait for DOM to render before capturing
          await new Promise((resolve) => setTimeout(resolve, 50));

          // Capture tile as canvas image using html2canvas
          // Using scale: 2 for balance between quality and file size
          const canvas = await html2canvas(tempContainer, {
            scale: 2,
            backgroundColor: "#ffffff",
            useCORS: true,
            allowTaint: true,
            logging: false,
            width: 2400,
            height: 2400,
          });

          const imgData = canvas.toDataURL("image/jpeg", 0.8); // Use JPEG with 80% quality for better compression

          // Add section header - use inclusive row/column numbers (1-based for display)
          pdf.setFontSize(14);
          pdf.setFont("helvetica", "bold");
          const startRow = tileY * tileSize + 1; // 1-based display
          const endRowDisplay = Math.min((tileY + 1) * tileSize, chart.height); // Last row in this tile (1-based)
          const startCol = tileX * tileSize + 1; // 1-based display
          const endColDisplay = Math.min((tileX + 1) * tileSize, chart.width); // Last col in this tile (1-based)

          pdf.text(
            `Section ${tileY + 1}-${tileX + 1} (Rows ${startRow}-${endRowDisplay}, Cols ${startCol}-${endColDisplay})`,
            marginLeft,
            marginTop + 15,
          );

          // Add the tile image with proper margins
          const maxWidth = contentWidth - 20; // Leave some padding within content area
          const imgWidth = Math.min(maxWidth, contentWidth - 20);
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Ensure image doesn't exceed content area
          const maxImgHeight = contentHeight - 60; // Leave space for headers/footers
          const finalImgHeight = Math.min(imgHeight, maxImgHeight);
          const finalImgWidth = (canvas.width * finalImgHeight) / canvas.height;

          pdf.addImage(
            imgData,
            "JPEG",
            marginLeft,
            marginTop + 35,
            finalImgWidth,
            finalImgHeight,
          );

          // Add page number and footer info
          pdf.setFontSize(8);
          pdf.setFont("helvetica", "normal");
          pdf.text(
            `${title} - Page ${tileNumber} of ${totalTiles}`,
            marginLeft,
            pageHeight - marginBottom + 10,
          );
          pdf.text(
            `Section ${tileY + 1}-${tileX + 1}`,
            pageWidth - marginRight - 30,
            pageHeight - marginBottom + 10,
          );
        }
      }

      // Add instructions page if requested
      // Compile instructions with abbreviation preference
      let instructionsForLargeChart = compiledInstructions;
      if (chart) {
        const compiled = compilePattern(chart, abbrevNames);
        instructionsForLargeChart = compilePatternToText(compiled);
      }
      
      if (includeInstructions && instructionsForLargeChart) {
        pdf.addPage();

        // Render instructions as HTML element and convert to image
        // Include the header in the HTML so it stays with the content
        const instructionsHtml = `<div style="font-size: 16px; font-weight: bold; margin-bottom: 8px;">Written Instructions:</div>${renderInstructionsAsHTML(instructionsForLargeChart)}`;
        const tempInstructionsContainer = document.createElement("div");
        tempInstructionsContainer.style.position = "absolute";
        tempInstructionsContainer.style.left = "-9999px";
        tempInstructionsContainer.style.top = "-9999px";
        tempInstructionsContainer.style.width = `${contentWidth}mm`;
        tempInstructionsContainer.style.padding = "10px";
        tempInstructionsContainer.style.backgroundColor = "#ffffff";
        tempInstructionsContainer.style.fontFamily = "monospace";
        tempInstructionsContainer.style.fontSize = "10px";
        tempInstructionsContainer.style.lineHeight = "1.8";
        tempInstructionsContainer.innerHTML = instructionsHtml;
        document.body.appendChild(tempInstructionsContainer);

        try {
          await new Promise((resolve) => setTimeout(resolve, 50));
          const canvas = await html2canvas(tempInstructionsContainer, {
            scale: 2,
            backgroundColor: "#ffffff",
            useCORS: true,
            allowTaint: true,
            logging: false,
          });

          const imgData = canvas.toDataURL("image/png");
          const imgWidth = contentWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          let instructionY = marginTop;

          // Handle multi-page instructions
          let remainingHeight = imgHeight;
          let sourceY = 0;
          const maxPageHeight = pageHeight - marginBottom - instructionY - 10;

          while (remainingHeight > 0) {
            if (instructionY + remainingHeight > pageHeight - marginBottom - 20) {
              const pageImgHeight = Math.min(remainingHeight, maxPageHeight);
              const sourceHeight = (pageImgHeight / imgHeight) * canvas.height;

              // Create a temporary canvas for this page slice
              const pageCanvas = document.createElement("canvas");
              pageCanvas.width = canvas.width;
              pageCanvas.height = sourceHeight;
              const ctx = pageCanvas.getContext("2d");
              ctx.drawImage(
                canvas,
                0,
                sourceY,
                canvas.width,
                sourceHeight,
                0,
                0,
                canvas.width,
                sourceHeight,
              );

              const pageImgData = pageCanvas.toDataURL("image/png");
              pdf.addImage(
                pageImgData,
                "PNG",
                marginLeft,
                instructionY,
                imgWidth,
                pageImgHeight,
              );

              remainingHeight -= pageImgHeight;
              sourceY += sourceHeight;

              if (remainingHeight > 0) {
                pdf.addPage();
                instructionY = marginTop;
              }
            } else {
              pdf.addImage(
                imgData,
                "PNG",
                marginLeft,
                instructionY,
                imgWidth,
                remainingHeight,
              );
              break;
            }
          }
        } catch (error) {
          console.error("Error rendering instructions:", error);
          // Fallback to plain text
          pdf.setFontSize(10);
          pdf.setFont("helvetica", "normal");
          const lines = instructionsForLargeChart.split("\n");
          let instructionY = marginTop + 25;
          lines.forEach((line) => {
            if (instructionY > pageHeight - marginBottom - 20) {
              pdf.addPage();
              instructionY = marginTop;
            }
            pdf.text(line, marginLeft, instructionY);
            instructionY += 6;
          });
        } finally {
          document.body.removeChild(tempInstructionsContainer);
        }
      }
    } finally {
      // Clean up temporary container
      document.body.removeChild(tempContainer);
    }

    // Save the PDF
    try {
      pdf.save(`${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf`);
    } catch (error) {
      console.error("PDF save failed:", error);
      throw new Error(
        "PDF export failed. The chart may be too large. Try reducing the quality setting or chart size.",
      );
    }
  };

  // Create HTML content for a specific tile
  // Fix: Include the last row of each tile by using <= instead of <
  // This ensures all rows are included (e.g., rows 1-50, then 51-100, not 1-49 then 51-99)
  const createTileContent = (tileX, tileY, tileSize) => {
    // Calculate row boundaries (0-based indices)
    const startRow = tileY * tileSize;
    // Calculate the exclusive end row (first row NOT in this tile)
    // Then subtract 1 to get the last row that IS in this tile
    const exclusiveEndRow = Math.min((tileY + 1) * tileSize, chart.height);
    const inclusiveEndRow = Math.max(startRow, exclusiveEndRow - 1); // Ensure >= startRow
    
    // Calculate column boundaries (0-based indices)
    const startCol = tileX * tileSize;
    const exclusiveEndCol = Math.min((tileX + 1) * tileSize, chart.width);
    const inclusiveEndCol = Math.max(startCol, exclusiveEndCol - 1); // Ensure >= startCol

    let html =
      '<table style="border-collapse: collapse; font-family: Roboto Mono, monospace; font-size: 24px; font-weight: bold;">';

    // Add column headers - render in reverse order (Column 1 at right, highest column at left)
    html += "<tr>";
    // Empty cell for row number space on left
    html +=
      '<td style="width: 40px; height: 40px; border: 1px solid #ccc; background-color: #f5f5f5; text-align: center;"></td>';
    // Iterate backwards through columns to display right-to-left numbering
    for (let displayCol = inclusiveEndCol; displayCol >= startCol; displayCol--) {
      const displayColNumber = displayCol + 1; // 1-based column number for display
      html += `<td style="width: 48px; height: 40px; border: 1px solid #ccc; background-color: #f5f5f5; text-align: center; font-size: 16px;">${displayColNumber}</td>`;
    }
    // Empty cell for row number space on right
    html +=
      '<td style="width: 40px; height: 40px; border: 1px solid #ccc; background-color: #f5f5f5; text-align: center;"></td>';
    html += "</tr>";

    // Add data rows - render in reverse order (Row 1 at bottom, highest row at top)
    // Iterate backwards through the rows in this tile
    for (let displayRow = inclusiveEndRow; displayRow >= startRow; displayRow--) {
      const displayRowNumber = displayRow + 1; // 1-based row number
      // In the round: all rows are worked right to left, so numbers always on right
      // Flat knitting: alternate based on RS/WS
      const isRightSide = inTheRound ? true : displayRowNumber % 2 === 1;
      
      html += "<tr>";
      // Row number on LEFT for WS rows (even, flat only), empty cell for RS rows (odd) or all in-the-round rows
      html += `<td style="width: 40px; height: 48px; border: 1px solid #ccc; background-color: #f5f5f5; text-align: center; font-size: 16px;">${!isRightSide ? displayRowNumber : ""}</td>`;

      // Data cells - render in reverse column order (Column 1 at right, highest column at left)
      for (let displayCol = inclusiveEndCol; displayCol >= startCol; displayCol--) {
        const rowKey = `row${displayRow}`;
        const cellValue = chart.rows[rowKey]?.[displayCol] || "-";
        html += `<td style="width: 48px; height: 48px; border: 1px solid #ccc; text-align: center; background-color: white;">${cellValue}</td>`;
      }
      
      // Row number on RIGHT for RS rows (odd) or all in-the-round rows, empty cell for WS rows (even, flat only)
      html += `<td style="width: 40px; height: 48px; border: 1px solid #ccc; background-color: #f5f5f5; text-align: center; font-size: 16px;">${isRightSide ? displayRowNumber : ""}</td>`;
      html += "</tr>";
    }

    // Add column footer - bottom row showing column numbers
    html += "<tr>";
    // Empty cell for row number space on left
    html +=
      '<td style="width: 40px; height: 40px; border: 1px solid #ccc; background-color: #f5f5f5; text-align: center;"></td>';
    // Iterate backwards through columns to display right-to-left numbering
    for (let displayCol = inclusiveEndCol; displayCol >= startCol; displayCol--) {
      const displayColNumber = displayCol + 1; // 1-based column number for display
      html += `<td style="width: 48px; height: 40px; border: 1px solid #ccc; background-color: #f5f5f5; text-align: center; font-size: 16px;">${displayColNumber}</td>`;
    }
    // Empty cell for row number space on right
    html +=
      '<td style="width: 40px; height: 40px; border: 1px solid #ccc; background-color: #f5f5f5; text-align: center;"></td>';
    html += "</tr>";

    html += "</table>";
    return html;
  };

  const exportToJSON = (title) => {
    const patternData = {
      title,
      chart,
      compiledInstructions,
      exportedAt: new Date().toISOString(),
      version: "1.0",
    };

    const dataStr = JSON.stringify(patternData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const patternData = JSON.parse(e.target.result);

        // Validate the pattern data
        if (!patternData.chart || !patternData.title) {
          setSnackbar({ open: true, message: "Invalid pattern file. Missing required data.", severity: "error" });
          return;
        }

        // Call the import handler
        if (onImportPattern) {
          onImportPattern(patternData);
        }

        // Reset file input
        event.target.value = "";
        onClose();
      } catch (error) {
        console.error("Error parsing JSON:", error);
        setSnackbar({ open: true, message: "Error reading pattern file. Please check the file format.", severity: "error" });
      }
    };
    reader.readAsText(file);
  };

  const handleImportClick = () => {
    if (fileInputRef) {
      fileInputRef.click();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box className="d-flex align-items-center gap-2">
          <Download color="primary" />
          Export Pattern
        </Box>
        <Typography variant="body2" color="text.secondary">
          Select your export options and then click the Export button to save
          your pattern.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <FormControl component="fieldset" className="mb-4">
              <FormLabel component="legend">Export Format</FormLabel>
              <RadioGroup value={exportFormat} onChange={handleFormatChange}>
                <FormControlLabel
                  value="pdf"
                  control={<Radio />}
                  label={
                    <Box className="d-flex align-items-center gap-2">
                      <PictureAsPdf color="error" />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          PDF Document
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Professional print-ready format
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="json"
                  control={<Radio />}
                  label={
                    <Box className="d-flex align-items-center gap-2">
                      <Code color="primary" />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          JSON File
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Save and reload your pattern data
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </RadioGroup>
              <Typography
                variant="caption"
                color="text.secondary"
                className="mt-1"
              >
                Tip: Use JSON format to save your work and reload it in the
                chart maker later. PDFs are great for sharing or printing.
              </Typography>
            </FormControl>

            {exportFormat === "pdf" && (
              <FormControl component="fieldset" className="mb-4">
                <FormLabel component="legend">Page Orientation</FormLabel>
                <RadioGroup
                  value={orientation}
                  onChange={handleOrientationChange}
                  row
                >
                  <FormControlLabel
                    value="portrait"
                    control={<Radio />}
                    label="Portrait"
                  />
                  <FormControlLabel
                    value="landscape"
                    control={<Radio />}
                    label="Landscape"
                  />
                </RadioGroup>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  className="mt-1"
                >
                  Landscape is better for wide charts, Portrait for tall charts
                </Typography>
              </FormControl>
            )}
          </div>

          {exportFormat === "pdf" && (
            <div className="col-12 col-md-6">
              <FormControl component="fieldset" className="mb-4">
                <FormLabel component="legend">Include Content</FormLabel>
                <Box className="d-flex flex-column gap-1">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeTitle}
                        onChange={(e) => setIncludeTitle(e.target.checked)}
                      />
                    }
                    label="Pattern Title"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeChart}
                        onChange={(e) => setIncludeChart(e.target.checked)}
                      />
                    }
                    label="Chart Grid"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeInstructions}
                        onChange={(e) =>
                          setIncludeInstructions(e.target.checked)
                        }
                      />
                    }
                    label="Written Instructions"
                  />

                  {/* Stitch Key options nested under Include Content */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeKey}
                        onChange={(e) => setIncludeKey(e.target.checked)}
                      />
                    }
                    label="Include Stitch Key"
                  />
                  <Box className="ms-4 d-flex flex-column gap-1">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={abbrevNames}
                          onChange={(e) => setAbbrevNames(e.target.checked)}
                          disabled={!includeKey && !includeInstructions}
                        />
                      }
                      label={
                        <Box className="d-flex flex-column align-items-start">
                          <Typography variant="body2">
                            Abbreviate Stitch Names
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            e.g. Knit → K (applies to stitch key and written
                            instructions)
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeDescriptions}
                          onChange={(e) =>
                            setIncludeDescriptions(e.target.checked)
                          }
                          disabled={!includeKey}
                        />
                      }
                      label="Include stitch descriptions"
                    />
                  </Box>
                </Box>
              </FormControl>
            </div>
          )}

          {exportFormat === "pdf" && (
            <FormControl component="fieldset" className="mb-4">
              <FormLabel component="legend">Export Quality</FormLabel>
              <RadioGroup value={quality} onChange={handleQualityChange} row>
                <FormControlLabel
                  value="low"
                  control={<Radio />}
                  label="Low (3x)"
                />
                <FormControlLabel
                  value="medium"
                  control={<Radio />}
                  label="Medium (4x)"
                />
                <FormControlLabel
                  value="high"
                  control={<Radio />}
                  label="High (6x)"
                />
                <FormControlLabel
                  value="ultra"
                  control={<Radio />}
                  label="Ultra (8x)"
                />
              </RadioGroup>
              <Typography
                variant="caption"
                color="text.secondary"
                className="mt-1"
              >
                Higher quality = larger file size but better readability for
                large charts
              </Typography>
            </FormControl>
          )}
        </div>

        {!hideImportSection && (
          <>
            <Divider className="my-3" />

            <Box className="mb-3">
              <Typography variant="h6" className="mb-2">
                Import Pattern
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                className="mb-3"
              >
                Load a previously exported JSON pattern file.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={handleImportClick}
                fullWidth
              >
                Choose JSON File
              </Button>
              <input
                type="file"
                accept=".json"
                className="hidden-input"
                ref={setFileInputRef}
                onChange={handleImport}
              />
            </Box>
          </>
        )}

        {/* Export Progress Indicator */}
        {isExporting && (
          <Box className="mt-3">
            <Typography variant="body2" color="text.secondary" className="mb-2">
              {exportProgress.message || "Exporting..."}
            </Typography>
            {exportProgress.total > 0 ? (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={(exportProgress.current / exportProgress.total) * 100}
                  className="mb-1"
                />
                <Typography variant="caption" color="text.secondary">
                  {exportProgress.current} of {exportProgress.total} sections
                  complete
                </Typography>
              </Box>
            ) : (
              <LinearProgress />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={isExporting}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          color="primary"
          startIcon={
            isExporting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Download />
            )
          }
          disabled={isExporting}
        >
          {isExporting ? "Exporting..." : "Export"}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ExportModal;
